// ABOUTME: Vue helpers for Hub save hearts on listing and community cards.
// ABOUTME: Fills hearts from saved-state and toggles /api/v1/hub/items/.

import { reactive, watch } from 'vue';

export function useHubCardHearts(getRows, kind) {
    const savedHearts = reactive({});
    const heartBusy = reactive({});
    const isCommunity = kind === 'community';

    function isHeartSaved(targetId) {
        return Boolean(savedHearts[targetId]);
    }

    function clearHearts() {
        Object.keys(savedHearts).forEach((key) => {
            delete savedHearts[key];
        });
    }

    // Bumped per load so a slow reply for an earlier page cannot repaint a newer one.
    let loadToken = 0;

    async function loadSavedHearts(rows) {
        if (typeof window.fetchHubSavedState !== 'function') {
            return;
        }
        const token = ++loadToken;
        const ids = (rows || []).map((row) => row.id).filter(Boolean);
        if (!ids.length) {
            clearHearts();
            return;
        }
        try {
            const data = await window.fetchHubSavedState(
                isCommunity ? { communityIds: ids } : { listingIds: ids },
            );
            if (token !== loadToken) {
                return;
            }
            const matches = isCommunity ? data.communities : data.listings;
            const idKey = isCommunity ? 'community_id' : 'listing_id';
            // Swapped in one go rather than cleared up front, so hearts already
            // on screen do not empty and refill while this is in flight.
            clearHearts();
            (matches || []).forEach((row) => {
                savedHearts[row[idKey]] = row.id;
            });
        } catch (error) {
            // Hearts stay as they are if saved-state cannot be read.
        }
    }

    async function toggleHeart(row) {
        if (!row || heartBusy[row.id] || typeof window.toggleHubSave !== 'function') {
            return;
        }
        const rowId = row.id;
        // Read before painting: the saved item id is what tells the API this is
        // an unsave, and the optimistic paint below would wipe it.
        const savedItemId = savedHearts[rowId] || null;
        heartBusy[rowId] = true;

        if (savedItemId) {
            delete savedHearts[rowId];
        } else {
            savedHearts[rowId] = true;
        }

        try {
            const result = await window.toggleHubSave({
                kind: isCommunity ? 'community' : 'home',
                communityId: isCommunity ? rowId : undefined,
                listingId: isCommunity ? undefined : rowId,
                // A listing row's `name` is its community; the home is its address.
                name: (isCommunity ? row.name : row.address || row.name) || '',
                savedItemId: savedItemId,
                onDismiss: () => {
                    delete savedHearts[rowId];
                },
                onUndo: (itemId) => {
                    savedHearts[rowId] = itemId;
                },
            });
            if (result.saved) {
                savedHearts[rowId] = result.id;
            } else {
                delete savedHearts[rowId];
            }
        } catch (error) {
            if (savedItemId) {
                savedHearts[rowId] = savedItemId;
            } else {
                delete savedHearts[rowId];
            }
            window.showHubSaveFailedToast(isCommunity ? 'community' : 'home');
        } finally {
            delete heartBusy[rowId];
        }
    }

    watch(getRows, (rows) => {
        loadSavedHearts(rows);
    }, { immediate: true });

    return { savedHearts, heartBusy, isHeartSaved, toggleHeart };
}
